<script lang="ts" setup>
import { ref, watch, nextTick } from 'vue';
import { createPopper, Instance as PopperInstance, Placement } from '@popperjs/core';

defineOptions({
    name: 'ideo-tooltip',
});

const props = defineProps({
  "tooltip": { default: '' },
  "position": { default: 'top' },
  "multiline": { type: Boolean, default: false },
  "forceFullWidth": { type: Boolean, default: false }
});

const wrapperRef = ref<HTMLElement | null>(null);
const tooltipRef = ref<HTMLElement | null>(null);
const arrowRef = ref<HTMLElement | null>(null);
const popperInstance = ref<PopperInstance | null>(null);
const isShown = ref(false);

const show = () =>
{
    if (props.tooltip)
    {
        isShown.value = true;
    }
};

const hide = () =>
{
    isShown.value = false;
};

watch(isShown, async (isVisible) =>
{
    if (isVisible)
    {
        await nextTick();

        if (wrapperRef.value && tooltipRef.value && arrowRef.value)
        {
            popperInstance.value = createPopper(wrapperRef.value, tooltipRef.value, {
                placement: props.position,
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 8],
                        },
                    },
                    {
                        name: 'arrow',
                        options: {
                            element: arrowRef.value,
                        },
                    },
                ],
            });
        }
    }
    else
    {
        if (popperInstance.value)
        {
            popperInstance.value.destroy();
            popperInstance.value = null;
        }
    }
});
</script>

<template>
    <div
        ref="wrapperRef"
        :class="{ forceFullWidth: props.forceFullWidth }"
        class="ideo-tooltip-wrapper"
        @mouseenter="show"
        @mouseleave="hide"
    >
        <slot />
        <Teleport to="body">
            <div
                v-if="isShown"
                ref="tooltipRef"
                class="ideo-tooltip-popper"
                :class="{ multiline: props.multiline }"
                role="tooltip"
            >
                {{ props.tooltip }}
                <div ref="arrowRef" class="ideo-tooltip-arrow" data-popper-arrow></div>
            </div>
        </Teleport>
    </div>
</template>

<style lang="scss">
.ideo-tooltip-wrapper {
    &.forceFullWidth {
        width: 100%;
    }
}

.ideo-tooltip-popper {
    --tooltip-bg: var(--bs-gray-600, #6c757d);
    --tooltip-color: var(--bs-white, #fff);
    --arrow-size: 6px;

    background: var(--tooltip-bg);
    color: var(--tooltip-color);
    font-family: Helvetica, sans-serif;
    font-size: 0.9em;
    padding: 0.25rem 0.5rem;
    border-radius: 0.3rem;
    box-shadow: 0 1em 2em -0.5em rgba(0, 0, 0, 0.35);
    z-index: 10001;
    min-width: 3em;
    max-width: 21em;
    text-align: center;
        white-space: normal;
    overflow: visible;

    &.multiline {
        min-width: 18em;
        line-height: 1.375;
        white-space: pre-wrap;
    }

    .ideo-tooltip-arrow {
        position: absolute;
        width: calc(var(--arrow-size) * 2);
        height: calc(var(--arrow-size) * 2);
        z-index: -1;
        background: transparent;
        pointer-events: none;

        &::before {
            content: '';
            position: absolute;
            border-style: solid;
        }
    }
}

/* 1. TOOLTIP NA GÓRZE -> Strzałka w dół */
.ideo-tooltip-popper[data-popper-placement^='top'] > .ideo-tooltip-arrow {
    bottom: calc(var(--arrow-size) * -1);

    &::before {
        bottom: 0;
        left: 0;
        border-width: var(--arrow-size) var(--arrow-size) 0 var(--arrow-size);
        border-color: var(--tooltip-bg) transparent transparent transparent;
    }
}

/* 2. TOOLTIP NA DOLE -> Strzałka w górę */
.ideo-tooltip-popper[data-popper-placement^='bottom'] > .ideo-tooltip-arrow {
    top: calc(var(--arrow-size) * -1);

    &::before {
        top: 0;
        left: 0;
        border-width: 0 var(--arrow-size) var(--arrow-size) var(--arrow-size);
        border-color: transparent transparent var(--tooltip-bg) transparent;
    }
}

/* 3. TOOLTIP Z LEWEJ -> Strzałka w prawo */
.ideo-tooltip-popper[data-popper-placement^='left'] > .ideo-tooltip-arrow {
    right: calc(var(--arrow-size) * -1);

    &::before {
        right: 0;
        top: 0;
        border-width: var(--arrow-size) 0 var(--arrow-size) var(--arrow-size);
        border-color: transparent transparent transparent var(--tooltip-bg);
    }
}

/* 4. TOOLTIP Z PRAWEJ -> Strzałka w lewo */
.ideo-tooltip-popper[data-popper-placement^='right'] > .ideo-tooltip-arrow {
    left: calc(var(--arrow-size) * -1);

    &::before {
        left: 0;
        top: 0;
        border-width: var(--arrow-size) var(--arrow-size) var(--arrow-size) 0;
        border-color: transparent var(--tooltip-bg) transparent transparent;
    }
}
</style>
